<template>
  <div class="product-variation-selector">
    <button
      v-if="variations.length < 4"
      v-for="variation in variations"
      class="product-variation-selector__button"
      :class="isSelected(variation)"
      :key="variation.id"
      @click.prevent="changeSelected(variation.databaseId)"
    >
      <span class="product-variation-selector__button-text">{{ variation.variationName }}</span>
      <span
        v-if="getVariationBadge(variation.databaseId)"
        class="product-variation-selector__badge"
      >
        {{ getVariationBadge(variation.databaseId) }}
      </span>
    </button>
    <select
      v-else v-model="selectedOption"
      id="variation-selector"
      name="variation-selector"
      class="product-variation-selector__dropdown-selector"
    >
      <option v-for="variation in variations" :key="variation.id" :value="variation.databaseId">
        <span>{{ variation.variationName }}</span>
        <span
          v-if="getVariationBadge(variation.databaseId)"
          class="product-variation-selector__dropdown-selector--badge"
        >
          &nbsp;-&nbsp;{{ getVariationBadge(variation.databaseId) }} off
        </span>
      </option>
    </select>
  </div>
</template>

<script>
import { usePriceStore } from "~/stores/priceStore";
import { useUserStore } from "~/stores/user";

export default {
  props: {
    selectedProductId: Number,
    deliveryOption: String,
    variations: {
      type: Object,
      required: true,
    },
  },
  setup() {
    return {
      priceStore: usePriceStore(),
      userStore: useUserStore(),
    }
  },
  data() {
    return {
      clientSideBadges: new Map(),
      useClientSideBadges: false,
      selectedOption: this.selectedProductId,
    }
  },
  computed: {
    currentCurrency() {
      return this.userStore?.getSelectedCurrency;
    },
  },
  watch: {
    currentCurrency() {
      this.recalculateBadgesForCurrency();
    },
    selectedProductId(newVal) {
      this.selectedOption = newVal
    },
    selectedOption(newVal) {
      this.changeSelected(newVal)
    }
  },
  created() {
    if (this.variations.length) {
      this.changeSelected(this.variations[0].databaseId)
    }
  },
  methods: {
    isSelected(variation) {
      return {
        'product-variation-selector__button--selected': variation.databaseId === this.selectedProductId,
      };
    },
    changeSelected(newProductId) {
      this.$emit('variation-changed', newProductId)
    },
    getVariationBadge(variationId) {
      // Use client-side badge if currency was changed
      if (this.useClientSideBadges && this.clientSideBadges.has(variationId)) {
        const badges = this.clientSideBadges.get(variationId);
        return this.deliveryOption === 'subscription' ? badges.subscription : badges.buyOut;
      }

      // Otherwise use server-side badge
      const variation = this.variations.find(v => v.databaseId === variationId);

      if (this.deliveryOption === 'subscription') {
        return variation?.productSubscriptionOptions?.percentageDiscount ? `${variation.productSubscriptionOptions.percentageDiscount}%` : null
      }

      return variation?.productSaleBadges?.percentageSaleBadge || null;
    },
    async recalculateBadgesForCurrency() {
      if (process.server) return;

      try {
        this.useClientSideBadges = true;
        this.clientSideBadges.clear();

        // Fetch prices for all variations with refresh to clear cache
        for (const variation of this.variations) {
          const priceData = await this.priceStore.getPrice(variation.databaseId, true);

          if (priceData) {
            const subscriptionDiscount = priceData.productSubscriptionOptions?.percentageDiscount;
            this.clientSideBadges.set(variation.databaseId, {
              buyOut: priceData.percentageDiscount ? `${priceData.percentageDiscount}%` : null,
              subscription: subscriptionDiscount ? `${subscriptionDiscount}%` : null,
            });
          }
        }
      } catch (error) {
        console.error('Error recalculating variation badges:', error);
        this.useClientSideBadges = false;
      }
    },
  },
}
</script>

<style lang="scss">
.product-variation-selector {
  display: flex;
  justify-content: space-evenly;
  gap: 0.3rem;
  margin-top: 0.7rem;

  &__button {
    align-items: center;
    border-radius: 2rem;
    border: 1px solid #000;
    display: flex;
    flex-grow: 1;
    gap: 0.4rem;
    justify-content: center;
    padding: 0.5rem 1rem;
    transition: color .25s, background-color .25s;

    &--selected {
      background-color: #000;
      color: #fff;
    }
  }

  &__button-text {
    flex-shrink: 1;
  }

  &__dropdown-selector {
    border: solid 1px;
    padding: 0.5rem 1rem;
    border-radius: 2rem;
    text-align: left;

    &--badge {
      background-color: var(--color-orange);
      font-weight: bold;
    }
  }

  &__badge {
    align-items: center;
    background-color: var(--color-orange);
    border-radius: 0.3rem;
    color: #fff;
    display: flex;
    flex-shrink: 0;
    font-size: 0.65rem;
    font-weight: bold;
    padding: 0.15rem 0.3rem;
  }
}
</style>